import { Injectable, inject, signal } from '@angular/core';
import { ApiClient } from '../api/api-client';

interface MeFeaturesResponse {
  // Backend returns FeatureModule enum names as strings:
  // ["Sandbox", "Environment", "EnvNoise"].
  features: string[];
  /**
   * Per-user capabilities, distinct from the tenant-wide feature list above.
   * A feature says "this tenant bought the module"; a capability says "this
   * signed-in user may do this particular thing inside it". They ride the same
   * response because the client needs both at the same moment — the alternative
   * was a second endpoint fetched on every navigation.
   *
   * Currently one pair: "EnvAuditFollowup.View" / "EnvAuditFollowup.Edit",
   * derived from the reserved `Compliance / EnvAuditFollowup` rows in
   * user_permissions (the ESG team's access to the two follow-up fields on an
   * audit finding).
   *
   * Coarse on purpose: permissions are per plant, this list says "granted on at
   * least one plant". It drives what the UI offers, never what the API returns —
   * AuditFindingService re-decides per finding, and it is the only authority.
   */
  capabilities?: string[];
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private api = inject(ApiClient);
  readonly enabled = signal<Set<string>>(new Set());
  readonly capabilities = signal<Set<string>>(new Set());
  private loadPromise: Promise<void> | null = null;

  async load(): Promise<void> {
    this.loadPromise = this.doLoad();
    return this.loadPromise;
  }

  // Cached load — first call triggers fetch, subsequent calls share the same in-flight
  // promise. Guards must await this so a direct-URL navigation (no prior sidebar click)
  // doesn't race past an empty Set and bounce the user to home.
  ensureLoaded(): Promise<void> {
    if (!this.loadPromise) this.loadPromise = this.doLoad();
    return this.loadPromise;
  }

  private async doLoad(): Promise<void> {
    try {
      const res = await this.api.get<MeFeaturesResponse>('/me/features');
      this.enabled.set(new Set(res.features ?? []));
      this.capabilities.set(new Set(res.capabilities ?? []));
    } catch {
      // 401 (not logged in yet) or network error → keep empty sets.
      // Routes guarded by feature flags will simply not be reachable, and every
      // capability reads as denied — the safe direction for both.
      this.enabled.set(new Set());
      this.capabilities.set(new Set());
    }
  }

  // Force a refresh (e.g., after tenant switch). Drops the cached promise.
  reset(): void {
    this.loadPromise = null;
    this.enabled.set(new Set());
    this.capabilities.set(new Set());
  }

  has(name: string): boolean { return this.enabled().has(name); }

  /** Per-user capability check. See `capabilities` on the response above. */
  can(capability: string): boolean { return this.capabilities().has(capability); }
}
